import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isEmpty, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteSpacesMutation,
  useSetSpacesAsActiveMutation,
  useSetSpacesAsInactiveMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  usePermissions,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WarehouseSpaces() {
  const navigate = useNavigate();

  const [warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const [deleteSpaces] = useDeleteSpacesMutation();
  const [setSpacesAsActive] = useSetSpacesAsActiveMutation();
  const [setSpacesAsInactive] = useSetSpacesAsInactiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(id)) {
          return logError('warehouseSpaceId is not defined');
        }

        navigate(composePath(settingsRoutes.warehousesSpacesDetail, {params: {id}}));
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        if (isEmpty(ids)) {
          return logError('warehouseSpaceId is not defined');
        }

        openDeleteDialog({
          text: i18n.t('entity.warehouse.labels.deleteSpacePrompt'),
          onConfirm: () =>
            deleteSpaces({body: {spaceIds: ids}})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .with('setAsActive', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        if (isEmpty(ids)) {
          return logError('warehouseSpaceId is not defined');
        }

        setSpacesAsActive({body: {spaceIds: ids}})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setAsInactive', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        if (isEmpty(ids)) {
          return logError('warehouseSpaceId is not defined');
        }

        setSpacesAsInactive({body: {spaceIds: ids}})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.spaces'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.labels.newSpace'),
        onClick: () => navigate(settingsRoutes.warehousesSpacesCreate),
        isDisabled: !warehouseDictionaryCreate,
        tooltip: !warehouseDictionaryCreate
          ? i18n.t('entity.warehouse.labels.missingWarehouseDictionaryCreatePermission')
          : undefined,
        'data-testid': testIds.settings.warehousesSpaces('actions.new'),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.spaces')} />
      <SettingsTemplate header={header} data-testid="settings-warehouse-spaces">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseSpace}
          actionCallback={actionCallback}
          data-testid={testIds.settings.warehousesSpaces('warehouseSpaces')}
        />
      </SettingsTemplate>
    </>
  );
}
